import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useNavLinksContext } from '../contexts/NavLinksContext';
import { useConfirmSubscription } from '../api/subscriptions';
import checkIcon from '../assets/images/checked.png';

const SubscriptionSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setNavLinks } = useNavLinksContext();
  const confirmMutation = useConfirmSubscription();
  const syncAttempted = useRef(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');
  const txRef = searchParams.get('tx_ref');
  const status = searchParams.get('status');
  const transactionId = searchParams.get('transaction_id');
  const subscriptionId = searchParams.get('subscription_id');

  useEffect(() => {
    setNavLinks({
      links: [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/subscription', label: 'Subscription' },
        { to: '/subscription/success', label: 'Success' },
      ],
    });
  }, [setNavLinks]);

    useEffect(() => {
    if (syncAttempted.current) return;
    
    // Handle Stripe callback (legacy)
    if (sessionId) {
      syncAttempted.current = true;
      const planId = searchParams.get('plan_id');

      confirmMutation.mutate(
        {
          plan_id: planId ? Number(planId) : 0,
          payment_method: 'stripe',
          payment_details: { session_id: sessionId },
        },
        {
          onSuccess: () => {
            setConfirmed(true);
            toast.success('Subscription activated successfully!');
          },
          onError: (err) => {
            setError(err.message);
            toast.error(err.message || 'Failed to confirm subscription');
          },
        }
      );
    }
    // Handle Flutterwave callback
    else if (txRef && (status === 'successful' || status === 'completed')) {
      syncAttempted.current = true;

      // No API call needed - backend processes webhook
      setConfirmed(true);
      toast.success('Payment successful! Your subscription will be activated shortly.');
    }
    // Handle Polar callback (redirect after hosted checkout)
    else if (subscriptionId && !txRef && !sessionId) {
      syncAttempted.current = true;
      setConfirmed(true);
      toast.success('Payment successful! Your subscription will be activated shortly.');
    }
    else if (status === 'cancelled') {
      syncAttempted.current = true;
      setError('Payment was cancelled');
      toast.info('Payment was cancelled');
    }
    else if (status && status !== 'successful' && status !== 'completed') {
      syncAttempted.current = true;
      setError(`Payment failed: ${status}`);
      toast.error(`Payment failed: ${status}`);
    }
  }, [sessionId, txRef, status, transactionId, subscriptionId, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!confirmed) return;
    const timer = setTimeout(() => navigate('/subscription'), 5000);
    return () => clearTimeout(timer);
  }, [confirmed, navigate]);

    if (!sessionId && !txRef && !subscriptionId) {
    return (
      <div className="h-fill-from-navbar flex items-center justify-center bg-bg_grey_light">
        <div className="bg-light rounded-xl p-8 max-w-md text-center shadow-md">
          <h2 className="text-xl font-bold text-dark mb-2">Invalid Session</h2>
          <p className="text-grey_dark mb-6">
            No checkout session found. Please try subscribing again.
          </p>
          <button
            onClick={() => navigate('/subscription')}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            Back to Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-fill-from-navbar flex items-center justify-center bg-bg_grey_light">
      <div className="bg-light rounded-xl p-10 max-w-md text-center shadow-md">
        {error ? (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <span className="text-red-600 text-2xl font-bold">!</span>
            </div>
            <h2 className="text-xl font-bold text-dark mb-2">Something Went Wrong</h2>
            <p className="text-grey_dark mb-6">{error}</p>
            <button
              onClick={() => navigate('/subscription')}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              Back to Plans
            </button>
          </>
        ) : confirmed ? (
          <>
            <img src={checkIcon} alt="Success" className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-dark mb-2">Payment Successful!</h2>
            <p className="text-grey_dark mb-6">
              Your subscription has been activated. You will be redirected shortly.
            </p>
            <button
              onClick={() => navigate('/subscription')}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              Go to Subscription
            </button>
          </>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-dark mb-2">Confirming Your Subscription</h2>
            <p className="text-grey_dark">
              Please wait while we activate your plan...
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
